/** Due-date buckets for My work (Home spec 2026-09-23 §5.1; SA-1174 adds
 * "This week", design pass spec §7.1). Pure: `now` and the deployment zone are
 * arguments, so a test pins a day boundary. */
export type DueBucket = 'Overdue' | 'Today' | 'This week' | 'Later' | 'No due date'
export const BUCKETS: DueBucket[] = ['Overdue', 'Today', 'This week', 'Later', 'No due date']

/** The `?due=` word for each bucket (SA-1174, design note D11): what the bar's
 * links write and the Tasks page reads. */
export type DueParam = 'overdue' | 'today' | 'week' | 'later' | 'none'
export const DUE_PARAM: Record<DueBucket, DueParam> = {
  Overdue: 'overdue', Today: 'today', 'This week': 'week', Later: 'later', 'No due date': 'none',
}
/** The bucket as a run-in word: "7 overdue", "Nothing due this week." */
export const DUE_WORD: Record<DueBucket, string> = {
  Overdue: 'overdue', Today: 'today', 'This week': 'this week', Later: 'later', 'No due date': 'no due date',
}

/** The bucket a `?due=` value names; null for anything else (an unknown word is ignored, never an error). */
export function bucketFromParam(value: string | null | undefined): DueBucket | null {
  return BUCKETS.find((b) => DUE_PARAM[b] === value) ?? null
}

interface Due { due_at: string | null; overdue: boolean }

const dayFormats = new Map<string, Intl.DateTimeFormat>()
const weekdayFormats = new Map<string, Intl.DateTimeFormat>()
const formatter = (cache: Map<string, Intl.DateTimeFormat>, zone: string, make: () => Intl.DateTimeFormat) => {
  let f = cache.get(zone)
  if (!f) { f = make(); cache.set(zone, f) }
  return f
}

/** The day, in the given zone, as `YYYY-MM-DD` (F7: exported so a later
 * widget — Task 10 — reads a due date's day off the same rule instead of
 * copying it). */
export const dayKey = (d: Date, zone: string) =>
  formatter(dayFormats, zone, () => new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' })).format(d)

const WEEKDAY: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
const pad2 = (n: number) => String(n).padStart(2, '0')

/** `key` plus `days` calendar days — a date step on the key's own digits, never `+ 24 h`. */
function addDays(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number)
  const u = new Date(Date.UTC(y, m - 1, d + days))
  return `${u.getUTCFullYear()}-${pad2(u.getUTCMonth() + 1)}-${pad2(u.getUTCDate())}`
}

/** The `dayKey` of the deployment zone's coming Sunday — today's when today is Sunday. */
export function weekEndKey(now: Date, zone: string): string {
  const weekday = formatter(weekdayFormats, zone, () => new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short' })).format(now)
  return addDays(dayKey(now, zone), (7 - (WEEKDAY[weekday] ?? 0)) % 7)
}

interface Ctx { today: string; weekEnd: string; zone: string }
/** The zone's day and week end, built on first use (SA-1181 D10): an overdue-only,
 * undated or empty list never asks `Intl` for a format. */
function ctxFor(now: Date, zone: string): () => Ctx {
  let ctx: Ctx | undefined
  return () => (ctx ??= { today: dayKey(now, zone), weekEnd: weekEndKey(now, zone), zone })
}

/** Overdue is the wire's word (stamped by the timer scan or past due on the
 * server's clock, SA-375) — never re-derived here. */
function bucketOf(task: Due, ctxOf: () => Ctx): DueBucket {
  if (task.overdue) return 'Overdue'
  if (!task.due_at) return 'No due date'
  const due = new Date(task.due_at)
  if (Number.isNaN(due.getTime())) return 'No due date'
  const ctx = ctxOf()
  const dueDay = dayKey(due, ctx.zone)
  if (dueDay === ctx.today) return 'Today'
  // ISO day keys order as text; "This week" is empty on a Sunday by construction.
  if (dueDay > ctx.today && dueDay <= ctx.weekEnd) return 'This week'
  return 'Later'
}

export function dueBucket(task: Due, now: Date, zone: string): DueBucket {
  return bucketOf(task, ctxFor(now, zone))
}

export function groupByDue<T extends Due>(tasks: T[], now: Date, zone: string): Array<{ bucket: DueBucket; tasks: T[] }> {
  const ctx = ctxFor(now, zone)
  const by = new Map<DueBucket, T[]>(BUCKETS.map((b) => [b, []]))
  for (const t of tasks) by.get(bucketOf(t, ctx))!.push(t)
  return BUCKETS.map((bucket) => ({ bucket, tasks: by.get(bucket)! })).filter((g) => g.tasks.length > 0)
}
